import type { Command } from '@commander-js/extra-typings';
import { createCommand } from '@commander-js/extra-typings';
import {
  extensionsBuildResultSchema,
  getBuildMetadataPath,
} from '@wix/cli-astro-definitions';
import type { AstroCommandServices } from '@wix/cli-command-contract';
import { CliError, CliErrorCode, cliErrorToError } from '@wix/cli-error';
import type { CliErrorCtx } from '@wix/cli-plugin-core';
import { parseBuildError, parseStdErr } from '@wix/cli-plugin-core';
import { writeJson } from '@wix/cli-fs';
import {
  isStandaloneExtensionsProject,
  importExtensionsPackageNodeApi,
} from '@wix/cli-custom-extensions';
import type { ProjectModel } from '../model.js';

const standaloneBuild = async (model: ProjectModel) => {
  const isFallbackManifestBuild =
    process.env.WIX_CLI_FALLBACK_MANIFEST === 'true';
  let buildResult: unknown;

  try {
    const { build, generateFallbackManifest } =
      await importExtensionsPackageNodeApi(model.projectFolder);
    buildResult = isFallbackManifestBuild
      ? await generateFallbackManifest({
          rootDir: model.projectFolder,
        })
      : await build({ rootDir: model.projectFolder });
  } catch (e: unknown) {
    const failedToBuildExtensionsApp = new CliError({
      code: CliErrorCode.FailedToBuildExtensionsApp(),
      cause: e,
    });
    if (model.pluginsInterface) {
      const errors = parseBuildError(e);
      const errContext: CliErrorCtx =
        errors != null
          ? {
              type: 'project',
              errors,
            }
          : {
              type: 'cli-user',
              error: cliErrorToError(failedToBuildExtensionsApp),
            };

      void model.pluginsInterface.eventsEmitter.emit('cli-error', errContext);
    }
    throw failedToBuildExtensionsApp;
  }

  const parseResult = extensionsBuildResultSchema.safeParse(buildResult);

  if (!parseResult.success) {
    throw new CliError({
      code: CliErrorCode.InvalidExtensionsBuildMetadata(),
      cause: parseResult.error,
    });
  }

  await writeJson(
    getBuildMetadataPath(model.projectFolder),
    parseResult.data.metadata,
    { spaces: 2 }
  );
};

const astroBuild = async (model: ProjectModel, command: Command) => {
  const { execa } = await import('execa');

  const { getRepoType, createPackageManager } = await import(
    '@wix/package-manager'
  );

  const repoType = await getRepoType(model.projectFolder);
  const packageManager = await createPackageManager(repoType);

  const { file, args } = packageManager.getRunBinaryCmd(
    ['astro', 'build'].concat(command.args)
  );

  const reportFailure = (buildResult: unknown): never => {
    const failedToBuildError = new CliError({
      code: CliErrorCode.FailedToBuildAstroApp(),
      cause: buildResult,
    });
    if (model.pluginsInterface) {
      const projectError = parseStdErr(buildResult);
      const errContext: CliErrorCtx =
        projectError != null
          ? {
              type: 'project',
              errors: [projectError],
            }
          : {
              type: 'cli-user',
              error: cliErrorToError(failedToBuildError),
            };

      void model.pluginsInterface.eventsEmitter.emit('cli-error', errContext);
    }
    throw failedToBuildError;
  };

  if (process.env.WIX_CLI_FALLBACK_MANIFEST === 'true') {
    const fallback = await execa(file, args, {
      cwd: model.projectFolder,
      stdin: 'inherit',
      stdout: 'inherit',
      stderr: 'pipe',
      reject: false,
      env: { WIX_CLI_FALLBACK_MANIFEST: 'true' },
    });

    if (fallback.stderr.includes('WIX_FALLBACK_MANIFEST_COMPLETE')) {
      return;
    }

    if (fallback.stderr) {
      process.stderr.write(fallback.stderr);
    }
    reportFailure(fallback);
  } else {
    try {
      const subprocess = execa(file, args, {
        cwd: model.projectFolder,
        stdin: 'inherit',
        stdout: 'inherit',
        stderr: 'pipe',
      });

      subprocess.stderr?.pipe(process.stderr);

      await subprocess;
    } catch (e: unknown) {
      reportFailure(e);
    }
  }
};

export const buildCommand = (
  services: AstroCommandServices,
  cliVersion: string
) =>
  createCommand('build')
    .description(
      'Invoke the astro build process, forwarding all command-line arguments.'
    )
    .allowExcessArguments(true)
    .allowUnknownOption(true)
    .passThroughOptions(true)
    .action(async (_, command) => {
      const { runCommand } = await import('./render-command.js');

      await runCommand(command, services, cliVersion, async (model) => {
        if (await isStandaloneExtensionsProject(model.projectFolder)) {
          await standaloneBuild(model);
        } else {
          await astroBuild(model, command);
        }
      });
    });
